<template>
  <div class="rtable-wrapper">

    <!-- LOGGING
    <div v-for="item in items" class="mb-1 caption" :key="item.tempID">
      {{ item }}
    </div>
    -->

    <!-- View mode selection -->
    <v-layout v-if="!barebones" class="ma-0" align-center justify-end>

      <v-btn
      icon
      :color="viewMode === 0 ? 'light-blue' : ''"
      flat
      @click="viewMode = 0">
        <v-icon>view_list</v-icon>
      </v-btn>

      <v-btn
      icon
      :color="viewMode === 1 ? 'light-blue' : ''"
      flat
      @click="viewMode = 1">
        <v-icon>phone_android</v-icon>
      </v-btn>

    </v-layout>

    <!-- TOP toolbar -->

      <div v-if="!barebones" :class="['toolbar-top']" ref="headerContainer" :style="{top: topOffset + 'px'}">
        <div style="position: relative;">

          <div :class="['header-sticky', {'shadowheader': headerIsSticky}]">
          </div>

          <!-- row filter bar -->
          <c-transition-expand>
            <div
            v-if="viewMode === 1 && !showSearchBar">
              <div
              class="hscroller">
                <div class="searchcol-button-bar">
                  <div
                  v-ripple
                  v-for="(header, index) in headers"
                  :key="index"
                  :class="['hitem unselectable', {'active': localSortIndex === index}]"
                  @mousedown="mouseDownHeader(index, $event)"
                  @mouseup="mouseUp(index, $event)"
                  @click="clickHeader(index)">
                    {{ header }}
                    <v-icon 
                    small
                    :class="[{'rotate-anim': index === localSortIndex && localSortAscending}, localSortIndex === index ? 'r-visible' : 'r-invisible']">
                      arrow_downward
                    </v-icon>
                  </div>
                </div>
              </div>
            </div>
          </c-transition-expand>

          <!-- end row filter bar -->

          <!-- search bar -->
          <c-transition-expand>
            <div
            v-if="viewMode === 1" class="pb-1 pt-1">

              <v-layout align-center justify-center class="ma-0">
                <v-btn
                v-if="showSearchBar"
                flat
                color="light-blue"
                icon
                class="ma-0"
                @click="showSearchBar = false">
                  <v-icon>arrow_back</v-icon>
                </v-btn>

                <v-text-field
                color="primary"
                spellcheck="false"
                placeholder="Search.."
                hide-details
                single-line
                solo
                clearable
                :class="['searchbar c-text-field-shrink', {'searchbar30': !showSearchBar && !$vuetify.breakpoint.xs}, {'searchbar80': !showSearchBar && $vuetify.breakpoint.xs}]"
                v-model="localSearchText"
                ref="searchBarMobile"
                @click.native="showSearchBar = true"
                ></v-text-field>
              </v-layout>

              <c-transition-expand>
                <div
                v-if="showSearchBar"
                class="c-flex-c1">
                  <div
                  class="hscroller">
                    <div class="searchcol-button-bar">
                      <div
                      v-ripple
                      v-for="(header, index) in headers"
                      :key="index"
                      :class="['caption hitem unselectable', {'active': searchColumn === index}]"
                      @click="searchColumn = index">
                        {{ header }}
                      </div>
                    </div>
                  </div>

                  <!-- number of search matches -->
                  <v-layout class="ma-0 caption" justify-center>
                    {{ searchMatches.length }} Match{{ searchMatches.length === 1 ? '' : 'es'}}
                  </v-layout>
                </div>
              </c-transition-expand>

            </div>
          </c-transition-expand>
          <!-- search bar -->

          <!-- selection bar -->
          <c-transition-expand>
            <div v-if="viewMode === 1 && rowIsSelected">
              <v-layout
              align-center
              justify-center
              class="ma-0 pa-2 subheading">
                <v-btn
                icon
                flat
                color="light-blue"
                class="ma-0"
                @click="selected = {}">
                  <v-icon>close</v-icon>
                </v-btn>
                <v-spacer></v-spacer>
                Selected <span class="selected-chip blue-grey darken-2">{{ selectedCount }}</span>
                <v-spacer></v-spacer>
                <div style="width: 36px;"></div>
              </v-layout>
            </div>
          </c-transition-expand>
          <!-- end selection bar -->

          <template
          v-if="viewMode === 0">
            <!-- extra top bar -->
            <div class="rtable-toolbar">
              <v-btn
              icon
              small
              flat
              class="ma-0"
              color="blue-grey darken-2"
              @click="entirePageIsSelected ? selected = {} : selectPage()">
                <v-icon>
                  {{ entirePageIsSelected ? 'check_box_outline_blank' : 'select_all' }}
                </v-icon>
              </v-btn>

              <v-btn 
              flat 
              icon 
              class="ma-0"
              color="blue-grey darken-2"
              @click="onDelete">
                <v-icon>delete_outline</v-icon>
              </v-btn>

              <v-btn
              flat
              icon
              color="blue-grey darken-2"
              @click="showSearchBar = !showSearchBar">
                <v-icon>search</v-icon>
              </v-btn>

              <v-btn
              flat
              icon
              color="blue-grey darken-2"
              @click="showBorderRight = !showBorderRight">
                <v-icon>border_right</v-icon>
              </v-btn>

              <v-spacer></v-spacer>

              <span class="caption mr-3">
                Items per page:
              </span>

              <v-select
              class="pagination-select caption mr-3"
              :items="paginationOptions"
              hide-details
              v-model="rowsPerPage">

              </v-select>

              <span class="caption">
                {{ paginationText }}
              </span>

              <v-btn
              flat
              icon
              class="ma-0"
              color="blue"
              :disabled="!canPageDown"
              @click="goToPage(page - 1)">
                <v-icon>keyboard_arrow_left</v-icon>
              </v-btn>

              <v-btn
              flat
              icon
              color="blue"
              class="ma-0"
              :disabled="!canPageUp"
              @click="goToPage(page + 1)">
                <v-icon>keyboard_arrow_right</v-icon>
              </v-btn>

            </div>

            <!-- selection bar -->
            <c-transition-expand>
              <div v-if="rowIsSelected">
                <v-layout
                align-center
                justify-center
                class="ma-0 pa-2 subheading">
                  <v-btn
                  icon
                  flat
                  color="light-blue"
                  class="ma-0"
                  @click="selected = {}">
                    <v-icon>close</v-icon>
                  </v-btn>
                  <v-spacer></v-spacer>
                  Selected <span class="selected-chip blue-grey darken-2">{{ selectedCount }}</span>
                  <v-spacer></v-spacer>
                  <div style="width: 36px;"></div>
                </v-layout>
              </div>
            </c-transition-expand>
            <!-- end selection bar -->

            <!-- begin header table relative wrapper -->
            <div style="position: relative;">

              <div 
              :class="['left-col-line', {'shadowmode': showLeftBorder}]" 
              ref="leftColShadow1">
              </div>

              <div class="rtable-header-row-table-container" ref="tableHeaderRowContainer">
              <!-- extra top bar -->

                <!-- 
                ************************************************************
                HEADER TABLE
                ************************************************************
                  -->
                <table :class="['rtable-header-table', {'border-right': showBorderRight}]" ref="table1">
                  <colgroup ref="colgroup">
                    <col>
                    <col 
                    v-for="(header, index) in headers" 
                    :key="index">
                  </colgroup>
                  <thead>
                    <tr class="unselectable" ref="headerTableHeaderRow">

                      <th class="tool-col" ref="headerToolCol">
                        <div ref="toolHeaderCol" style="display: inline-block;">
                          <slot name="tool-header">
                          </slot>
                        </div>
                      </th>

                      <th 
                      v-for="(header, index) in headers" 
                      :key="index"
                      v-ripple
                      @click="clickHeader(index)"
                      @mousedown="mouseDownHeader(index, $event)"
                      @mouseup="mouseUp(index, $event)"
                      :class="[{'sticky-col': index==0}, {'active-header': localSortIndex === index}]">
                        {{ header }}
                        <v-icon 
                        small
                        :class="[{'rotate-anim': index === localSortIndex && localSortAscending}, localSortIndex === index ? 'r-visible' : 'r-invisible']">
                          arrow_downward
                        </v-icon>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    
                    <!-- SEARCH ROW -->
                    <tr v-if="showSearchBar">
                      <td class="tool-col"></td>
                      <td v-for="(header, index) in headers"
                      :key="index"
                      :class="{'sticky-col': index==0}">

                        <v-text-field 
                        placeholder='Search'
                        clearable
                        hide-details>
                        </v-text-field>
                        
                      </td>
                    </tr>

                  </tbody>
                </table>

              </div>

            </div>
            <!-- end header table relative wrapper -->
          </template>

        </div>
        <!-- END OF TOP TOOLBAR -->
      </div>
      <!-- end toolbar -->

      <!-- table container, to contain left border -->      
      <div style="position: relative;">

      <div 
      :class="['left-col-line', {'shadowmode': showLeftBorder}]" 
      ref="leftColShadow2">
      </div>

      <div class="rtable-scroller" @scroll="onScroll" ref="tableContainer">
        <template
        v-if="viewMode === 0">
          <table :class="['rtable', {'border-right': showBorderRight}]" ref="table2">

            <colgroup ref="colgroup1">
              <col>
              <col 
              v-for="(header, index) in headers" 
              style="width: 150px;" :key="index">
            </colgroup>

            <draggable 
            :list="localItems"
            :element="'tbody'"
            :options="{animation: 150, handle: '.drag-handle'}"
            @end="endDrag">

              <slot 
              v-for="item in paginatedArray" 
              name="table-row" 
              :item="item" 
              :selected="selected" 
              :leftOffset="leftOffset"
              :searchText="localSearchText"
              :sortIndex="localSortIndex">
              </slot>

            </draggable>
          </table>
        </template>

        <!-- 
        **************************************************************
        PHONE ROW
        **************************************************************
        -->

        <template
        v-else-if="viewMode === 1">
          
          <draggable
          :list="localItems"
          :element="'div'"
          :options="{animation: 150, handle: '.drag-handle'}"
          @end="endDrag">

            <slot v-for="item in paginatedArray" 
            name="phone-row" 
            :item="item" 
            :selected="selected" 
            :leftOffset="leftOffset"
            :lastVisited="item[primaryKey] === lastMobileRowClicked"
            :searchText="localSearchText"
            :sortIndex="localSortIndex">
            </slot>

          </draggable>
          
        </template>

      </div>

      </div>
    <!-- end position relative -->

    <!-- pagination on bottom -->

    <div class="text-xs-center">
      <v-pagination
      v-if="paginationLength > 1"
      v-model="page"
      :length="paginationLength"
      circle>

      </v-pagination>
    </div>

     <!-- toolbar -->
    <div 
    v-if="!barebones"
    class="toolbar-centering-wrapper">
      <div class="rtable-row-toolbar">

        <slot name="mobile-toolbar"></slot>

        <template v-if="rowIsSelected">
          <v-btn 
          flat
          @click="onDelete"
          class="c-btn-vert-caption">
            <v-icon color="light-blue">delete_outline</v-icon>
            <span class="caption-xs blue-grey--text text--darken-1">Delete</span>
          </v-btn>
            
        </template>

        <v-btn 
        flat
        @click="entirePageIsSelected ? selected = {} : selectPage()"
        class="c-btn-vert-caption">
          <v-icon color="light-blue">{{ entirePageIsSelected ? 'check_box_outline_blank' : 'select_all' }}</v-icon>
          <span class="caption-xs blue-grey--text text--darken-1">{{entirePageIsSelected ? 'Deselect all' : 'Select page'}}</span>
        </v-btn>

        <v-btn 
        flat
        @click="clickSearch"
        class="c-btn-vert-caption">
          <v-icon color="light-blue">search</v-icon>
          <span class="caption-xs blue-grey--text text--darken-1">Search</span>
        </v-btn>

      </div>
    </div>

      <!-- DIALOG TIME -->
      <v-dialog 
      v-model="dialog" 
      content-class="green-dialog" 
      scrollable 
      persistent 
      transition="c-anim-slide-left-fade"
      :fullscreen="$vuetify.breakpoint.xs" 
      max-width="400px">

        <v-card class="dialog-root-card">
          
          <v-card-title class="dialog-title-bar">
            <v-layout align-center>
              <v-btn
              icon
              class="light-blue--text"
              round @click.native="dialog = false">
                <v-icon>close</v-icon>
              </v-btn>
              <v-spacer></v-spacer>
              <span class="title font-weight-medium black--text">
                Edit
              </span>
              <v-spacer></v-spacer>
              <v-btn
              icon
              flat
              class="light-blue--text"
              round @click.native="mobileUpdateRow()">
                <v-icon>check</v-icon>
              </v-btn>
            </v-layout>
          </v-card-title>
          
          <v-card-text class="dialog-card-body">
            <slot 
            v-if="delayedDialogSlot" 
            name="dialog-row"
            :item="dialogRow" 
            :selected="selected" 
            :leftOffset="leftOffset"
            :sortIndex="localSortIndex">
            </slot>
          </v-card-text>
        </v-card>

      </v-dialog>

  </div>
</template>

<script>
import Vue from 'vue'
import Draggable from 'vuedraggable'
import ScrollParent from 'scrollparent'

export default {
  components: {
    Draggable
  },
  props: {
    barebones: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default: function () {
        return []
      }
    },
    headers: {
      type: Array,
      default: function () {
        return []
      }
    },
    sort: {
      type: Function,
      default: function (headerIndex, items, sortAscending) {
        return items
      }
    },
    sortIndex: {
      default: 0
    },
    sortAscending: {
      type: Boolean,
      default: true
    },
    search: {
      type: Function,
      default: function (searchText, searchColumn, items) {
        return items
      }
    },
    searchText: {
      default: ''
    },
    leftOffset: {
      default: 0
    },
    topOffset: {
      default: 0
    },
    primaryKey: {
      default: 'tempID'
    },
    scrollParent: {
      default: null
      /*
      This will be set on mounted () and enables the drop shadow on top header once
      a certain scroll height is set. Its basically a hack bacause browsers dont support
      showing a class when position: sticky changes to position: fixed
      Options:
      null = auto detect scroll parent
      'window' (string) = window
      or a reference
      */
    },
    ordering: {
      type: String,
      default: null
      /*
      Default null means no ordering will be used. Otherwise specify the name of
      the ordering column
      */
    }
    /*
    *********************************
    emissions:
    searchMatchCount -> number of search matches
    *********************************
    */
  },
  data () {
    return {
      viewMode: 1, // 0 = table, 1 = phone
      showLeftBorder: false,
      headerIsSticky: false,
      drag: {
        dragging: false,
        startX: 0,
        startWidth: 0,
        distance: 0,
        index: null,
        startTime: 0
      },
      tempIDcounter: 0, // counter
      localSortIndex: this.sortIndex,
      selected: {},
      localSortAscending: this.sortAscending,
      localScrollParent: this.scrollParent,
      // pagination
      page: 1,
      rowsPerPage: 10,
      paginationOptions: [
        5,
        10,
        20,
        50
      ],
      showSearchBar: false,
      showBorderRight: true,
      searchColumn: 0,
      dialog: false,
      delayedDialogSlot: false,
      dialogRow: {},
      dialogRowToOverwrite: null,
      lastMobileRowClicked: null,
      lastTrackedLocalItemsSize: 0
    }
  },
  computed: {
    localSearchText: {
      get () {
        return this.searchText
      },
      set (nv) {
        this.$emit('update:searchText', nv)
      }
    },
    localItems: {
      // This allows drag and drop so props aren't directly manipulated
      get () {
        return this.items
      },
      set (nv) {
        this.$emit('update:items', nv)
      }
    },
    searchMatches () {
      return this.search(this.localSearchText, this.searchColumn, this.localItems)
    },
    sortedItems () {
      return this.sort(this.localSortIndex, this.searchMatches, this.localSortAscending)
    },
    paginatedArray () {
      var index = (this.page - 1) * this.rowsPerPage
      return this.sortedItems.slice(index, index + this.rowsPerPage)
    },
    canPageDown () {
      return this.page > 1
    },
    canPageUp () {
      return (this.page) * this.rowsPerPage < this.sortedItems.length
    },
    paginationText () {
      /* var left = (this.page - 1) * this.rowsPerPage
      var remainder = this.sortedItems.length - left
      var right = left + Math.min(remainder, this.rowsPerPage)
      return left + ' - ' + right + ' of ' + this.sortedItems.length */
      return 'Page ' + this.page + ' of ' + this.paginationLength
    },
    paginationLength () {
      return Math.ceil(this.sortedItems.length / this.rowsPerPage)
    },
    rowIsSelected () {
      return Object.values(this.selected).indexOf(true) >= 0
    },
    selectedCount () {
      return Object.values(this.selected).reduce((a, i) => a + (i === true), 0)
    },
    entirePageIsSelected () {
      for (var item of this.paginatedArray) {
        if (this.selected[item.tempID] !== true) {
          return false
        }
      }
      return true
    }
  },
  watch: {
    dialog (nv) {
      if (nv) {
        this.delayedDialogSlot = true
      } else {
        setTimeout(() => { this.delayedDialogSlot = this.dialog }, 400)
      }
    },
    localItems: {
      immediate: true,
      handler (nv) {
        // detect addition or removal to the array
        if (nv.length !== this.lastTrackedLocalItemsSize) {
          // Array has experienced an addition or delete
          if (this.ordering !== null) {
            nv.sort((a, b) => a[this.ordering] - b[this.ordering])
            for (const [index, item] of nv.entries()) {
              item[this.ordering] = index
            }
          }
        }
        this.lastTrackedLocalItemsSize = nv.length
        // generate tempID
        nv.map(item => {
          if (!item.tempID) {
            item.tempID = this.getNewID()
          }
        })
      }
    },
    sortedItems: {
      immediate: true,
      handler (nv) {
        this.$emit('update:searchMatchCount', nv.length)
      }
    },
    selectedCount: {
      immediate: true,
      handler (nv) {
        this.$emit('update:selectedCount', nv)
      }
    },
    showSearchBar (nv) {
      this.localSearchText = ''
    },
    page (nv) {
      var top = window.pageYOffset + this.$refs.tableContainer.getBoundingClientRect().top - this.$refs.headerContainer.getBoundingClientRect().height - 48
      // do a threshold check to prevent annoying shaking when scrolling small increments
      if (Math.abs(window.pageYOffset - top) > 2) {
        this.$vuetify.goTo(top)
        // window.scrollTo(0, top) //instant snap to position
      }
    },
    localSearchText (nv) {
      this.page = 1
    },
    viewMode (nv) {
      if (nv === 0) {
        this.$nextTick(() => {
          this.initalizeTableLayout()
        })
      } else if (nv === 1) {
        this.initializeMobileLayout()
      }
    },
    rowsPerPage (nv) {
      this.page = 1
    },
    sortIndex (nv) {
      this.sortIndex = nv
    },
    localSortIndex (nv) {
      this.$emit('update:sortIndex', nv)
    },
    sortAscending (nv) {
      this.localSortAscending = nv
    },
    localSortAscending (nv) {
      this.$emit('update:sortAscending', nv)
    }
  },
  methods: {
    endDrag (event) {
      // reorder items
      var from = event.oldIndex
      var to = event.newIndex
      this.localItems.splice(to, 0, this.localItems.splice(from, 1)[0])
      for (const [index, item] of this.localItems.entries()) {
        item[this.ordering] = this.localSortAscending ? index : (this.localItems.length - 1 - index)
      }
    },
    clickSearch () {
      this.showSearchBar = !this.showSearchBar
      if (this.showSearchBar) {
        this.$nextTick(() => {
          this.$refs.searchBarMobile.focus()
        })
      }
    },
    selectPage () {
      for (var item of this.paginatedArray) {
        Vue.set(this.selected, item.tempID, true)
      }
    },
    // Drag functions
    onScroll (e) {
      if (e.target.scrollLeft > 0) {
        this.showLeftBorder = true
      } else {
        this.showLeftBorder = false
      }
      this.$refs.tableHeaderRowContainer.scrollLeft = e.target.scrollLeft
    },
    windowScroll (e) {
      var headerRect = this.$refs.headerContainer.getBoundingClientRect()
      var tableRect = this.$refs.tableContainer.getBoundingClientRect()
      if (tableRect.y - headerRect.y + 5 < headerRect.height) {
        this.headerIsSticky = true
      } else {
        this.headerIsSticky = false
      }
      // console.log(tableRect.y - headerRect.y + 5)
      // console.log(headerRect)
    },
    onMouseMove (e) {
      if (this.drag.dragging && this.viewMode === 0) {
        var distance = this.drag.startWidth + (e.pageX - this.drag.startX)
        this.drag.distance = Math.abs(this.drag.startX - e.pageX)
        if (distance < 20) { // minimum cell width (should be positive)
          distance = 20
        }
        this.$refs.colgroup.children[this.drag.index + 1].style.width = distance + 'px'
        this.$refs.colgroup1.children[this.drag.index + 1].style.width = distance + 'px'
        if (this.drag.index === 0) {
          var cool = this.$refs.headerToolCol.clientWidth + distance - 1
          this.$refs.leftColShadow1.style.left = cool + 'px'
          this.$refs.leftColShadow2.style.left = cool + 'px'
        }
      }
    },
    mouseDownHeader (index, e) {
      // @click="$refs['header' + index][0].style.width = '200px'"
      this.drag.dragging = true
      this.drag.startTime = Date.now()
      this.drag.distance = 0
      this.drag.index = index
      this.drag.startX = e.pageX
      var cell = e.currentTarget
      // var cell = e.target ? e.target : e.srcElement
      this.drag.startWidth = cell.clientWidth
    },
    mouseUp (index, e) {
      this.drag.dragging = false
      this.drag.index = null
    },
    // end DRAG functions
    getNewID () {
      this.tempIDcounter++
      return this.tempIDcounter
    },
    clickHeader (headerIndex) {
      var timeDiff = Date.now() - this.drag.startTime
      if (timeDiff > 200 || this.drag.distance > 8) {
        // drag startTime is only reset on mouse down. so it cant prevent firing
        // a click on header aswell as a drag
        return
      }
      if (this.localSortIndex !== headerIndex) {
        this.localSortAscending = false
      } else {
        this.localSortAscending = !this.localSortAscending
      }
      this.localSortIndex = headerIndex
    },
    goToPage (page) {
      if ((page - 1) * this.rowsPerPage < this.sortedItems.length && page >= 1) {
        this.page = page
        return true
      }
      return false
    },
    initializeMobileLayout () {
      this.$refs.tableContainer.style.overflowX = 'auto'
    },
    initalizeTableLayout () {
      this.$refs.tableContainer.style.overflowX = 'scroll'
      // allow it to measure scroll bar then proceed on nextTick
      this.$nextTick(() => {
        // Remeasure all cols
        for (var child of this.$refs.colgroup.children) {
          child.style.width = '150px'
        }
        // Continue
        var scrollBarHeight = this.$refs.tableContainer.offsetHeight - this.$refs.tableContainer.clientHeight
        this.$refs.leftColShadow2.style.bottom = scrollBarHeight + 'px'
        this.$refs.tableContainer.style.overflowX = 'auto'
        // this.leftOffset = this.$refs.toolHeaderCol.clientWidth + 20
        // add padding of 20 px
        this.$refs.colgroup.children[0].style.width = this.leftOffset + 'px'
        this.$refs.colgroup1.children[0].style.width = this.leftOffset + 'px'
        this.$refs.headerTableHeaderRow.children[1].style.left = this.leftOffset + 'px'
        // move the shadow
        this.$refs.leftColShadow1.style.left = this.leftOffset + 150 + 'px'
        this.$refs.leftColShadow2.style.left = this.leftOffset + 150 + 'px'
      })
    },
    // Dialog functions
    onRowClicked (row) {
      this.lastMobileRowClicked = row[this.primaryKey]
    },
    showDialog (row) {
      this.dialogRow = JSON.parse(JSON.stringify(row))
      this.dialogRowToOverwrite = row.tempID
      this.dialog = true
    },
    mobileUpdateRow () {
      this.dialog = false
      Object.assign(this.items[this.items.findIndex(el => el.tempID === this.dialogRowToOverwrite)], this.dialogRow)
    },
    onDelete () {
      var selectedTempIDs = Object.entries(this.selected).reduce((a, i) => {
        if (i[1]) {
          a.push(parseInt(i[0]))
        }
        return a
      }, [])
      var rows = this.items.filter(i => selectedTempIDs.includes(i.tempID))
      this.$emit('onDelete', rows, this.onSuccessfullDelete)
    },
    onSuccessfullDelete () {
      this.selected = {}
    },
    updateIDsAfterSave (responseRows) {
      // if a newly created row has been saved, it now has an id
      for (var row of responseRows) {
        this.items.find(item => {
          return item.tempID === row.tempID
        }).id = row.id
      }
    }
  },
  created () {
  },
  mounted () {
    if (this.localScrollParent === null) {
      this.localScrollParent = ScrollParent(this.$el)
    } else if (this.localScrollParent === 'window') {
      this.localScrollParent = window
    }
    this.localScrollParent.addEventListener('mouseup', this.mouseUp)
    this.localScrollParent.addEventListener('mousemove', this.onMouseMove)
    this.localScrollParent.addEventListener('scroll', this.windowScroll)
    if (this.viewMode === 0) {
      this.initalizeTableLayout()
    } else if (this.viewMode === 1) {
      this.initializeMobileLayout()
    }
  },
  destroyed () {
    console.log('destroying')
    console.log(this.localScrollParent)
    this.localScrollParent.removeEventListener('scroll', this.windowScroll)
  }
}
</script>

<style lang="scss">
.pagination-select {
  margin: 0px;
  padding: 0px;
  flex-grow: 0;
  flex-basis: 50px; 
  display: inline-flex;
}
.rtable-toolbar {
  background: white;
  display: flex;
  align-items: center;
  padding-left: 12px;
  padding-right: 12px;
  border-top: thin solid #cfd8dc;
  border-bottom: thin solid #cfd8dc;
}
.left-col-line {
  position: absolute;
  pointer-events: none;
  left: 217px;
  top: 0px;
  bottom: 0px;
  width: 12px;
  z-index: 1;
}
.left-col-line.shadowmode {
  background: linear-gradient(to left, transparent, rgba(0,0,0,0.15));
}
.toolbar-top {
  position: sticky;
  z-index: 2;
  background: white;
}
.rtable-header-row-table-container {
  overflow: hidden;
}

.header-sticky {
  height: 0px;
  background: #cfd8dc;
  left: 0px;
  right: 0px;
  bottom: 0px;
  position: absolute;
  border-bottom: thin solid rgba(0,0,0,0.2);
}

.header-sticky.shadowheader:after {
  pointer-events: none;
  content: "";
  left: 0px;
  right: 0px;
  height: 10px;
  position: absolute;
  background: linear-gradient(to top, transparent, rgba(0,0,0,0.15));
}
/* This is the only solution to centre a fixed (aka position sticky) div */
.toolbar-centering-wrapper {
  display: flex;
  justify-content: center;
  position:sticky;
  bottom: 15px;
  z-index: 1;
  pointer-events: none;
}
.rtable-row-toolbar {
  pointer-events: auto;
  display: flex;
  justify-content: center;
  /*    */
  padding: 0px 20px;
  border-radius: 40px;
  display: flex;
  align-items: center;
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.2);
  border: thin solid rgba(0,0,0,0.2);
  background: rgba(255, 255, 255, 0.95);
}
.rtable-wrapper {
  position: relative;
}
.rtable-scroller {
  overflow-x: scroll;
  /* initially set overflow-x to scroll so it can be measured on mounted () */
  overflow-y: visible;
}
.rtable {
  table-layout: fixed;
  width: 100px;
  /* Width is essential to allow colgroups to define cell width */
  border-collapse: separate;
  border-spacing: 0px;
  /* border-collapse: seperate and border-spacing: 0px prevents border-bottom of a cell to the
  right overlapping a cell to the left when in horizontal scroll mode resizing a column and 
  rescrolling left */
}
.rtable-header-table {
  table-layout: fixed;
  background: white;
  width: 100px;
  /* Width is essential to allow colgroups to define cell width */
  border-collapse: separate;
  border-spacing: 0px;
  /* border-collapse: seperate and border-spacing: 0px prevents border-bottom of a cell to the
  right overlapping a cell to the left when in horizontal scroll mode resizing a column and 
  rescrolling left */
}
.sticky-col {
  left: 68px;
  position: sticky;
  background: white;
  /* box-shadow: inset -1px 0px rgb(245, 248, 250), 2px 0px rgba(0,0,0,0.01); */
  z-index: 1;
}
.tool-col {
  left: 0px;
  position: sticky;
  background: white;
  z-index: 1;
  white-space: nowrap;
  border-bottom: none!important;
  border-right: none!important;
}
.tool-col > div {
  display: flex;
  align-items: center;
}
.rtable.border-right td {
  border-right: thin solid rgba(0, 0, 0, 0.15);
}
.rtable-header-table.border-right td, .rtable-header-table.border-right th{
  border-right: thin solid rgba(0, 0, 0, 0.15);
}
.rtable td {
  padding-left: 10px;
  padding-right: 10px;
  overflow: hidden;
  border-bottom: thin solid rgba(0,0,0,0.2);
  padding-top: 10px;
  padding-bottom: 10px;
  text-overflow: ellipsis;
}
.rtable-header-table td {
  padding: 4px;
}
.rtable tr:hover td {
  background: #f3f6f8;
}
.rtable tr {
  cursor: pointer;
}
.rtable-header-table th {
  padding-left: 10px;
  padding-right: 10px;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  color: #a7afb8;
  padding-top: 12px;
  padding-bottom: 12px;
  font-size: 12px;
  font-weight: 550;
  overflow: hidden;
  border-bottom: thin solid;
  text-overflow: ellipsis;
}
.rtable-header-table th i {
  color: #cfd8dc;
  transition: transform 300ms ease;
}
.rtable-header-table th:hover {
  color: #222;
}
.rtable-header-table th:hover i{
  opacity: 1;
  color: #cfd8dc;
}
.rtable .active-row td {
  background: #f3f6f8;
}
.rtable-header-table th.active-header {
  color: #03a9f4;
}
.rtable-header-table th.active-header i {
  color: #03a9f4!important;
}
.r-visible {
  opacity: 1;
}
.r-invisible {
  opacity: 0;
}
.drag-handle {
  cursor: move;
}

/* CRX INPUT MODIFICATIONS */
td .crx-input {
  margin-top: 0px;
}
td .crx-input::before {
  content: none;
}
/* Mobile List */

/* this will get called from row components */
.selected-chip {
  border-radius: 3px;
  color: white;
  padding: 0px 6px;
  margin-left: 4px;
}
.hitem {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: bold;
  color: #a7afb8;
}
.hitem:hover {
  color: #455a64;
}
.hitem:first-child {
  padding-left: 12px;
}
.hitem:last-child {
  border-right: none;
  padding-right: 12px;
}
.hitem:hover i {
  opacity: 1;
}
.hitem.active {
  color: #03a9f4;
  color: #455A64;
  /* background: linear-gradient(30deg, #B388FF, #00d2bd); */
}
.hitem.active i {
  color: #03a9f4!important;
}
.searchcol-button-bar {
  display: flex;
  margin: auto;
  /* box-shadow: 0px 1px 3px rgba(0,0,0,0.1); */
}
/* ANIMATIONS */
.rotate-anim {
  transform: rotate( -180deg );
}
.searchbar30 {
  flex: 0.3;
  min-width: 200px;

  & input {
    margin: 0px;
    font-size: 13px;
  }
}
.searchbar80 {
  flex: 0.8;
  min-width: 200px;

  & input {
    margin: 0px;
    font-size: 13px;
  }
}
.searchbar {
 transition: 0.15s all; 
}
.searchbar:focus-within .v-input__slot {
  box-shadow: inset 0px 0px 0px 1px #eee, 0px 1px 2px rgba(0,0,0,0.1)!important;
  /* box-shadow: 0 1px 6px 0 rgba(32,33,36,0.28)!important; */
  background: white!important;
}
.searchbar .v-input__slot {
  transition: 0.15s all; 
}
.searchbar input {
  margin: 5px;
  transition: 0.15s all; 
}
/* junk */
@keyframes pulse {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0.7;
  }
}
</style>

